import {Box, Button, Layer, ResponsiveContext, ThemeContext} from "grommet";
import React, {useContext} from "react";
import Section from "../../Section";
import {formatDate} from "../../../utils/dates";
import useSWR from "swr";
import {sentencecase, lowercase, unsnakecase} from "../../../utils/string";
import {formatCurrency} from "../../../utils/numbers";
import {NotificationsContext} from "../../NotificationsProvider";
import {handleErrors, handleFileDownloadErrors} from "../../../utils/errors";
import DataTableComponent from "../../DataTable";
import {csvFilename, getCSVConfig} from "../../../utils/csv";
import styled from "styled-components";
import {useRouter} from "next/router";
import {formatName} from "../../../utils/names";
import {downloadBlobAsCsvFile} from "../../../lib/exportcsv";
import {useSession} from "next-auth/react";
import CustomerTag from "../../CustomerTag";
import {BalanceDisplay} from "../../../pages/p/statement/[uuid]";
import InvoiceTag from "../../InvoiceTag";
import {
    faXmarkLarge as IconClose
} from '@fortawesome/pro-light-svg-icons';
import FontAwesomeIcon from "../../FontAwesomeIcon";
import MultipleInvoiceTags from "../../MultipleInvoiceTags";
const FloatingCloseButton = styled(Button)`
    ${props => props.isDarkBackground && !props.theme.dark && `
        &:hover {
            background-color: #00000022;
        }`}
`;

const PayoutDetails = ({id, onClose}) => {
    const notificationCtx = useContext(NotificationsContext);

    const fetcher = (url) => fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    }).then(handleErrors)
        .catch(notificationCtx.showNotification);

    const {data, error } = useSWR(
        `/api/reports/payouts/${id}`,
        fetcher,
        { revalidateOnFocus: false },
    );

    return data ? (
        <ResponsiveContext.Consumer>
            {(size) => (
                <Layer
                    position="bottom"
                    full="horizontal"
                    modal
                    onEsc={onClose}
                    round={{size: 'small', corner: 'top'}}
                    background={size === "small" ? 'background' : 'transparent'}
                >
                    <Box direction="column" gap="small">
                        <Box align="end" fill="horizontal" pad="small">
                            <ThemeContext.Consumer>
                                {(theme) => {
                                    return (
                                        <FloatingCloseButton
                                            onClick={onClose}
                                            label={<FontAwesomeIcon SelectedIcon={IconClose} size="small" color={(size === "small" || theme.dark) ? 'text' : 'neutral-8'}/>}
                                            isDarkBackground={size !== 'small'}
                                            theme={theme} />
                                    );
                                }}
                            </ThemeContext.Consumer>
                        </Box>
                        <Box
                            gap="small"
                            pad={{horizontal: 'large', vertical: 'medium'}}
                            background={size === "small" ? '' : 'background'}
                            border
                            round={size !== "small" && {size: 'small', corner: 'top'}}>
                            <Box
                                alignSelf="center"
                                width={{max: 'xxlarge'}}
                                fill="horizontal">
                                <Box
                                    align="center"
                                    justify="between"
                                    direction="row-responsive"
                                    margin={{top: 'small', bottom: size === "small" ? "large" : undefined}}
                                    gap="medium"
                                    pad={{top: 'small'}} >
                                    <Box margin={size == "small" ? {bottom: 'large'} : {}}>
                                        <Section
                                            title="Payout details"
                                            subheader={`Paid on ${formatDate(data.payout_date)}${data?.masked_account_number ? ` to ${data?.account_type ? lowercase(data?.account_type) : 'bank'} account ${data?.masked_account_number}` : ''}`}
                                            titleProps={{margin: '0'}}
                                            hideBorder
                                            contentProps={{pad: '0'}} />
                                    </Box>
                                    <Box direction="row" gap="medium" justify="between" margin={{vertical: size === "small" ? 'small' : 'none'}}>
                                        <BalanceDisplay size={size} number={formatCurrency(data.total, data.currency)} label="Gross amount" />
                                        <BalanceDisplay size={size} number={formatCurrency(data.disbursement_fee_amount, data.currency)} label="Fee" />
                                        <BalanceDisplay size={size} number={formatCurrency(data.disbursement_amount, data.currency)} label="Net amount" />
                                    </Box>
                                </Box>
                                <Box overflow="auto">
                                    <PayoutBreakdownTable payoutId={id} payments={data?.payments} refunds={data?.refunds} chargebacks={data?.chargebacks}/>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Layer>
            )}
        </ResponsiveContext.Consumer>
    ) : (<></>);
}

const PayoutBreakdownTable = ({payments, refunds, chargebacks, payoutId}) => {
    const {data: session} = useSession();
    const {company} = session || {};
    const notificationCtx = useContext(NotificationsContext);
    const router = useRouter();
    let data = [];
    if (payments) {
        data = [...payments.map(entry => ({
            ...entry,
            net_amount: ((entry.amount || 0) - (entry.fee || 0)),
            identifier: entry.order?.identifier || "",
            token: entry?.invoice?.invoice_token,
            customer: entry.recipient?.company_name || formatName(entry?.recipient, true),
            type: 'Payment'
        }))];
    }
    if (refunds) {
        data = [...data, ...refunds.map(entry => ({
            ...entry,
            net_amount: ((entry.amount || 0) - (entry.fee || 0)),
            identifier: entry.order?.identifier || "",
            token: entry?.invoice?.invoice_token,
            customer: entry.recipient?.company_name || formatName(entry?.recipient, true),
            type: 'Refund'
        }))];
    }
    if (chargebacks) {
        data = [...data, ...chargebacks.map(entry => ({
            ...entry,
            net_amount: ((entry.amount || 0) + (entry.fee || 0)),
            identifier: entry.order?.identifier || "",
            token: entry?.invoice?.invoice_token,
            customer: entry.recipient?.company_name || formatName(entry?.recipient, true),
            type: 'Chargeback'
        }))];
    }

    const getPayoutReportCSV = (id) => {
        const url = `/api/reports/payouts/payout-csv?id=${id}`;
        const exportError = {type: 'error', message: 'Unable to download csv data. Try again later.'};
        fetch(url, {
            method: 'GET'
        })
            .then(handleFileDownloadErrors)
            .then((data) => {
                data ?
                    downloadBlobAsCsvFile(
                        data,
                        csvFilename('payout_detail_report')
                    ) :
                    notificationCtx.showNotification(exportError);
            })
            .catch(e => notificationCtx.showNotification(exportError));
    };

    const debitTransactionType = ["Refund", "Chargeback"]

    const columns = [
        {
            selector: row => row.created_at,
            name: 'Date',
            sortable: true,
            grow: 1.5,
            sortField: 'created_at',
            cell: (datum) => formatDate(datum.created_at)
        },
        {
            selector: row => row.type,
            name: 'Type',
            sortField: 'type',
            sortable: true
        },
        {
            selector: row => row.identifier,
            name: 'Invoice',
            sortField: 'identifier',
            cell: (datum) => {
                if((datum?.linked_invoice_ids && datum?.linked_invoice_ids.length) > 1) {
                    return(<MultipleInvoiceTags ids={datum?.linked_invoice_ids} />)
                }
                if(datum?.referencable_id) {return(<InvoiceTag id={datum?.referencable_id}/>)}
                return(<></>)
            }
        },
        {
            selector: row => row.payment_source,
            name: 'Payment source',
            sortable: true,
            sortField: 'payment_source',
            cell: (datum) => sentencecase(unsnakecase(datum.payment_source))
        },
        {
            selector: row => row.customer,
            name: 'Customer',
            sortable: true,
            grow: 1.5,
            sortField: 'customer',
            cell: (datum) => datum?.recipient?.id && (<CustomerTag id={datum.recipient.id} />)
        },
        {
            selector: row => row.amount,
            name: 'Gross Amount',
            right: true,
            type: 'currency',
            cell: (datum) => formatCurrency((debitTransactionType.includes(datum.type) ? -1*datum.amount : datum.amount) || -0, datum.currency)
        },
        {
            selector: row => row.fee,
            name: 'Fees',
            right: true,
            type: 'currency',
            cell: (datum) => formatCurrency(datum.fee ? (datum.fee > 0 ? -1*datum.fee : datum.fee) : -0, datum.currency)
        },
        {
            selector: row => row.net_amount,
            name: 'Net Amount',
            right: true,
            type: 'currency',
            cell: (datum) => formatCurrency((debitTransactionType.includes(datum.type) ? -1*datum.net_amount : datum.net_amount) || -0, datum.currency)
        }
    ];

    return payments ? (
        <DataTableComponent
            data={data}
            columns={columns}
            isServer={false}
            onExportCSVClicked={() => getPayoutReportCSV(payoutId)}
            titleProps={{margin: {top: '0'}}}
            dataTableWrapperProps={{margin: "0"}}
            exportConfigs={getCSVConfig(columns)}
            onRowClicked={row => row?.token && router.push(`/invoices/${row.token}`)}
            dataName="payout_details" />
    ) : (<>No detailed breakdown available</>);
}

export default PayoutDetails;
